// src/pages/ViewStudent.jsx
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Button, TextField, Grid, Paper, Typography } from "@mui/material";
import { saveAs } from "file-saver";
import { FaEye } from "react-icons/fa";
import * as XLSX from "xlsx";
import api from "../api"; // নিশ্চিত করো src/api/index.js আছে
import "./Login.css";

const ViewStudent = () => {
  const [students, setStudents] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [filters, setFilters] = useState({ name: "", mobile: "", class: "", section: "" });
  const [loading, setLoading] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // helper to read school_id & token availability
  const ensureAuth = () => {
    const token = localStorage.getItem("token");
    const sid = localStorage.getItem("school_id");
    if (!token) {
      window.location.href = "/app/login";
      return false;
    }
    if (!sid) {
      console.warn("No school_id set in localStorage");
      // still allow; backend may reject without X-School-Id
    }
    return true;
  };

  // fetch students via central api (axios)
  const fetchStudents = async (page = 1, limit = perPage, currentFilters = filters) => {
    if (!ensureAuth()) return;
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };
      if (currentFilters.name) params.name = currentFilters.name;
      if (currentFilters.mobile) params.mobile = currentFilters.mobile;
      if (currentFilters.class) params.class = currentFilters.class;
      if (currentFilters.section) params.section = currentFilters.section;

      const res = await api.get("/view-student", { params });
      const payload = res?.data || {};

      // API earlier returned status: "success"
      if (payload && (payload.status === "success" || payload.status === true)) {
        setStudents(Array.isArray(payload.students) ? payload.students : []);
        setTotalRows(Number(payload.total || 0));
        setCurrentPage(Number(payload.page || page));
        setPerPage(Number(payload.limit || limit));
      } else {
        // if backend returns different shape, defensively handle
        setStudents(Array.isArray(payload.students) ? payload.students : []);
        setTotalRows(Number(payload.total || 0));
      }
    } catch (err) {
      console.error("fetchStudents error:", err?.response || err);
      // if 401 -> redirect to login
      if (err?.response?.status === 401) {
        // optional: clear token
        // localStorage.removeItem("token");
        window.location.href = "/app/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial load
    fetchStudents(1, perPage);
    // eslint-disable-next-line
  }, []);

  // Export to Excel
  const exportToExcel = () => {
    const filteredData = students.map((row, index) => {
      // be flexible with column names from backend
      const state = row.state || row.state_name || row.state_name || "";
      const district = row.district || row.district_name || row.district || "";
      const block = row.block || row.block_name || row.block || "";
      const className = row.class_name || row.class || "";
      const sectionName = row.section_name || row.section || "";

      return {
        "#": index + 1,
        "Student ID": row.student_code || row.student_id || "",
        "Name": row.name || "",
        "Mobile": row.mobile || "",
        "Class": className,
        "Section": sectionName,
        "Roll Number": row.roll_no || "",
        "State": state,
        "District": district,
        "Block": block,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "Students.xlsx");
  };

  // handle DataTable pagination events
  const handlePageChange = (page) => {
    fetchStudents(page, perPage);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchStudents(1, newPerPage);
  };

  // safe encode for view details
  const encodeId = (id) => {
    try {
      return btoa(String(id));
    } catch (e) {
      return encodeURIComponent(String(id));
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", marginTop: "0px" }}>
      <div style={{ display: "flex", flex: 1 }}>
        <div className="p-5" style={{ flex: 1, margin: "5px" }}>
          <Paper className="p-5" elevation={3} style={{ padding: "10px" }}>
            <Typography variant="h5" gutterBottom>View Students</Typography>
            <Grid container spacing={2} alignItems="center">
              {Object.keys(filters).map((key) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <TextField
                    fullWidth
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    value={filters[key]}
                    onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => fetchStudents(1, perPage, filters)}
                >
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button variant="contained" color="success" fullWidth onClick={exportToExcel}>
                  Export to Excel
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper className="mt-4 p-4" elevation={3} style={{ marginTop: "5px" }}>
            <DataTable
              columns={[
                {
                  name: "#",
                  selector: (row, index) => index + 1,
                  sortable: false
                },
                {
                  name: "Action",
                  cell: (row) => (
                    <button
                      onClick={() => (window.location.href = `/app/admin/view-student-details/${encodeId(row.student_id || row.id || row.student_code)}`)}
                      className="custom-btn"
                    >
                      <FaEye className="icon" /> View
                    </button>
                  ),
                  ignoreRowClick: true,
                  allowOverflow: true,
                  button: true
                },
                { name: "Student ID", selector: (row) => row.student_code || row.student_id || "", sortable: true },
                { name: "Name", selector: (row) => row.name || "", sortable: true },
                { name: "Mobile", selector: (row) => row.mobile || "", sortable: true },
                { name: "Class", selector: (row) => row.class_name || row.class || "", sortable: true },
                { name: "Section", selector: (row) => row.section_name || row.section || "", sortable: true },
                { name: "Roll Number", selector: (row) => row.roll_no || "", sortable: true },
                { name: "State", selector: (row) => row.state || row.state_name || "", sortable: true },
                { name: "District", selector: (row) => row.district || row.district_name || "", sortable: true },
                { name: "Block", selector: (row) => row.block || row.block_name || "", sortable: true },
              ]}
              data={students}
              progressPending={loading}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
            />
          </Paper>
        </div>
      </div>
    </div>
  );
};

export default ViewStudent;
